import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
    const { user, checked, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    if (checked && user) return <Navigate to="/" replace />;

    const handle = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        const res = await login(email.trim(), password);
        setBusy(false);
        if (res.ok) navigate("/");
        else setErr(res.error);
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2 relative" data-testid="login-page">
            <div className="hidden md:flex items-end p-14 relative overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1439792675105-701e6a4ab6f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxjYWxtJTIwZGFyayUyMG1vdW50YWluJTIwZGF3bnxlbnwwfHx8fDE3OTIxNzc4MDF8MA&ixlib=rb-4.1.0&q=85"
                    alt="Mountain at dawn"
                    className="absolute inset-0 w-full h-full object-cover opacity-50"
                />
                <div className="absolute inset-0 bg-[#0E0E10]/70" />
                <div className="relative z-10 max-w-md">
                    <div className="caption mb-4">A trader's quiet place</div>
                    <h1 className="font-serif text-4xl lg:text-5xl leading-tight">
                        Prepare with <span className="gold">discipline.</span>
                    </h1>
                    <h1 className="font-serif text-4xl lg:text-5xl leading-tight mt-1">
                        Act with <span className="gold">peace.</span>
                    </h1>
                    <p className="mt-6 text-zinc-400 leading-relaxed">
                        A personal command center for your routine, your journal, and your faith — all in one calm
                        place.
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 md:p-12">
                <form onSubmit={handle} className="w-full max-w-sm" data-testid="login-form">
                    <div className="font-serif text-2xl mb-1">Welcome back</div>
                    <p className="text-sm text-zinc-500 mb-8">Sign in to your trading command center.</p>

                    <label className="caption">Email</label>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="login-email"
                        className="mt-2 mb-5 w-full bg-[#18181B] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-[#D4AF37]/60"
                        placeholder="you@example.com"
                    />

                    <label className="caption">Password</label>
                    <div className="relative mt-2 mb-2">
                        <input
                            type={show ? "text" : "password"}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="login-password"
                            className="w-full bg-[#18181B] border border-white/10 rounded-md px-3 py-2.5 text-sm pr-10 focus:border-[#D4AF37]/60"
                        />
                        <button
                            type="button"
                            onClick={() => setShow(!show)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200 p-1"
                            aria-label="Toggle password"
                            data-testid="login-toggle-password"
                        >
                            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>

                    <div className="flex justify-end mb-4">
                        <Link
                            to="/forgot-password"
                            className="text-xs text-zinc-500 hover:text-[#D4AF37]"
                            data-testid="login-forgot-link"
                        >
                            Forgot password?
                        </Link>
                    </div>

                    {err && (
                        <div className="mb-4 px-3 py-2 bg-[#B95050]/10 border border-[#B95050]/30 rounded-md text-sm text-[#E8A5A5]" data-testid="login-error">
                            {err}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="login-submit"
                        className="w-full bg-[#D4AF37] text-[#0E0E10] font-medium px-4 py-2.5 rounded-md hover:bg-[#B3922E] transition-colors disabled:opacity-50"
                    >
                        {busy ? "Signing in..." : "Sign in"}
                    </button>

                    <div className="mt-6 text-center text-sm text-zinc-500">
                        No account?{" "}
                        <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="login-register-link">
                            Create one
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
